import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAnswer,
  updateNhsNumber,
  updateSectionItems,
  addNewItem,
  removeItem,
  clearCarePlan,
  loadCarePlanSections,
  loadCarePlanItems,
  setMode,
} from "../../../redux/slices/careplanSlice";
import styled from "styled-components";
import UrgentCare from "./UrgentCare";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import AddItemOverlay from "../CarePlanBuilder/AddItemOverlay";
import NHSNumberInput from "../../common/NHSNumberInput";
import NavigationButtons from "./NavigationButtons";
import MultiSelectDropdown from "./MultiSelectDropdown";
import {
  fetchSectionsData,
  fetchItemsData,
  checkCarePlanExists,
  createCarePlan,
  fetchDefaultContacts,
} from "../../../redux/actions/careplanActions";
import { useDashboardNav } from "../../../hooks/useDashboardNav";
import {
  CircleFilled,
  CircleUnfilled,
  PlusIcon,
  DeleteIcon,
} from "../../common/Icons";
import IconButton from "../../common/Button";
import TextField from "../../common/TextField";
import Spacer from "../../common/Spacer";
import ItemThumbnail from "../../common/ItemThumbnail";
import DropIndicator from "../../common/DropIndicator";

const CreateCarePlan = ({ onClose }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const go = useDashboardNav();

  const { sectionsLoaded, itemsLoaded, sections, items, carePlan } =
    useSelector((state) => state.careplan);

  const [activeSection, setActiveSection] = useState(0);
  const [nhsNumber, setNHSNumber] = useState("");
  const [isAddOtherItemOpen, setAddOtherItemOpen] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showFinishConfirmModal, setShowFinishConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemDescription, setNewItemDescription] = useState("");
  const [showAddItemOverlay, setShowAddItemOverlay] = useState(false);
  const [showNHSNumberWarning, setShowNHSNumberWarning] = useState(false);
  const [selectedItemIdsPerSection, setSelectedItemIdsPerSection] = useState(
    {}
  );
  const [initialized, setInitialized] = useState(false);
  const [validatedNhsNumber, setValidatedNhsNumber] = useState(""); // Store the validated NHS number which will be checked to allow the user to navigate through sidebar item once come back after already validated
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showSavedConfirmation, setShowSavedConfirmation] = useState(false); // After save popup confirmed, another popup need to be shown for that
  const [savedToDb, setSavedToDb] = useState(false);
  // draggable state for the user defined items
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [deleteText, setDeleteText] = useState("");

  const currentSection = carePlan.sections[activeSection] || {};
  const currentSectionId = currentSection.id;
  const isDraggable = currentSection.isServiceUserSpecific;

  useEffect(() => {
    // enter create mode as early as possible
    dispatch(setMode("create"));
    return () => {
      // leave create mode on unmount
      dispatch(setMode("view"));
    };
  }, [dispatch]);

  useEffect(() => {
    formRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [activeSection]);

  useEffect(() => {
    // Fetch sections data from the server if not fetched
    if (!sectionsLoaded) {
      dispatch(fetchSectionsData());
    }
  }, [sectionsLoaded, dispatch]);

  useEffect(() => {
    // Fetch items data from the server if not fetched
    if (!itemsLoaded && sectionsLoaded) {
      dispatch(fetchItemsData());
    }
  }, [itemsLoaded, sectionsLoaded, dispatch]);

  useEffect(() => {
    // load carePlan state variable using the loaded section and items if they are fetched already from server
    if (sectionsLoaded && itemsLoaded && !initialized) {
      // loading carePlan state variable as no unsaved care plan available in local storage
      dispatch(clearCarePlan());
      dispatch(loadCarePlanSections(sections));
      dispatch(loadCarePlanItems(items));
      dispatch(fetchDefaultContacts());
      setInitialized(true); // Mark initialization as done
    }
  }, [
    sectionsLoaded,
    itemsLoaded,
    initialized, //Using carePlan.sections.length in the dependency array instead of carePlan.sections prevents unnecessary re-renders.
    dispatch,
    sections,
    items,
    nhsNumber,
  ]);

  useEffect(() => {
    // setting user selected items for each sections while they are active to differentiate them from unselected items
    const sectionId = carePlan.sections[activeSection]?.id;
    if (sectionId) {
      const currentSelectedIds =
        carePlan.sections[activeSection].items?.map((item) => item.id) || [];
      setSelectedItemIdsPerSection((prevState) => ({
        ...prevState,
        [sectionId]: currentSelectedIds,
      }));
    }
  }, [activeSection, carePlan.sections]);

  const handleNHSNumberChange = (value) => {
    setNHSNumber(value);
  };

  const validateNHSNumber = (value) => {
    const nhsNumberPattern = /^\d{3}\s\d{3}\s\d{4}$/;
    return nhsNumberPattern.test(value);
  };

  const handleContinue = () => {
    if (activeSection === 0) {
      // Check for NHS Number format is met
      if (!validateNHSNumber(nhsNumber)) {
        return;
      }

      // If the format is met then call the api to check the NHS Number is already available
      dispatch(checkCarePlanExists(nhsNumber))
        .then((result) => {
          if (result.payload.exists) {
            // Show warning popup as the NHS Number is already existing
            setShowNHSNumberWarning(true);
          } else {
            // NHS number is valid and unique
            setValidatedNhsNumber(nhsNumber); // Store the validated NHS number to verify is it changed while navigating
            dispatch(updateNhsNumber(nhsNumber));
            if (activeSection < carePlan.sections.length - 1) {
              setActiveSection(activeSection + 1);
            }
          }
        })
        .catch((error) => {});
    } else {
      // Other sections
      if (activeSection < carePlan.sections.length - 1) {
        setActiveSection(activeSection + 1);
      }
    }
  };

  const handleBack = () => {
    if (activeSection > 0) {
      setActiveSection(activeSection - 1);
    }
  };

  const handleSave = () => {
    if (activeSection === 0) {
      // Check for NHS Number format is met
      if (!validateNHSNumber(nhsNumber)) {
        setShowNHSNumberWarning(true);
        return;
      }

      // If the format is met then call the api to check the NHS Number is already available
      dispatch(checkCarePlanExists(nhsNumber))
        .then((result) => {
          if (result.payload.exists) {
            // Show warning popup as the NHS Number is already existing
            setShowNHSNumberWarning(true);
          } else {
            // NHS number is valid and unique
            setValidatedNhsNumber(nhsNumber); // Store the validated NHS number to verify is it changed while navigating
            dispatch(updateNhsNumber(nhsNumber));
            setShowFinishConfirmModal(true);
          }
        })
        .catch((error) => {});
    } else {
      setShowFinishConfirmModal(true);
    }
  };

  const handleFinishConfirmation = (confirm) => {
    if (confirm) {
      dispatch(createCarePlan())
        .then(() => {
          // After creation and refetch, call the parent function for redirect to home
          setSavedToDb(true);
          setShowFinishConfirmModal(false);
          setShowSavedConfirmation(true);
        })
        .catch((error) => {});
    } else {
      setShowFinishConfirmModal(false);
    }
  };

  const handleAddNewItem = () => {
    // if (newItemTitle.trim() === "" || newItemDescription.trim() === "") {
    if (newItemTitle.trim() === "") {
      // Need to shwo an error
      return; // Do not add an empty item
    }

    const newItem = {
      id: `new-${Date.now()}`, // Temporary ID for the new item
      title: newItemTitle,
      description: newItemDescription,
      answer: "", // Initialize the answer as an empty string
    };

    dispatch(
      addNewItem({
        sectionId: carePlan.sections[activeSection].id,
        item: newItem,
      })
    );

    setNewItemTitle("");
    setNewItemDescription("");
    setAddOtherItemOpen(false);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Remove") {
      deleteTextField.setCustomValidity(
        'Please type "Remove" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }
    dispatch(
      removeItem({
        sectionId: carePlan.sections[activeSection].id,
        itemId: itemToDelete.id,
      })
    );
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
    setDeleteText("");
  };

  const handleUpdateSelectedItems = (updatedSelectedItemIds) => {
    const sectionId = carePlan.sections[activeSection].id;
    const existingItems = carePlan.sections[activeSection].items || [];
    const existingItemIds = existingItems.map((i) => i.id);

    // 1) Figuring out which IDs are truly new (i.e. weren’t already in the section)
    const newItemIds = updatedSelectedItemIds.filter(
      (id) => !existingItemIds.includes(id)
    );

    if (newItemIds.length) {
      // 2) Grab their full item objects, sort them by title, then dispatch addNewItem in that order
      const newItems = items
        .filter((item) => newItemIds.includes(item.id))
        .sort((a, b) => a.title.localeCompare(b.title));

      newItems.forEach((item) => {
        dispatch(
          addNewItem({
            sectionId,
            item: { ...item, fromOverlay: true },
          })
        );
      });
    }

    // 3) Remove only those overlay-added items that were deselected
    existingItems.forEach((existing) => {
      if (
        existing.fromOverlay &&
        !updatedSelectedItemIds.includes(existing.id)
      ) {
        dispatch(removeItem({ sectionId, itemId: existing.id }));
      }
    });

    // 4) Update the UI’s selected-IDs map
    setSelectedItemIdsPerSection((prev) => ({
      ...prev,
      [sectionId]: updatedSelectedItemIds,
    }));
  };

  const handleClose = () => {
    setShowCloseConfirmation(true); // Show confirmation for closing
  };

  const handleCloseConfirmation = (confirm) => {
    if (confirm) {
      // If user clicks Yes, go to Dashboard Overview
      dispatch(clearCarePlan());
      onClose(); // Close the overlay
    } else {
      // If user clicks No, stay in the Care Plan creation flow
      setShowCloseConfirmation(false);
    }
  };

  const handleDragStart = (e, itemId) => {
    setDraggingItemId(itemId); // Set the dragging item ID
    e.dataTransfer.setData("text/plain", itemId); // Required for Firefox
  };

  const handleDrop = (e, targetItemId) => {
    e.preventDefault();

    e.currentTarget.style.opacity = "0"; // Reset the opacity of the drop indicator

    if (draggingItemId === targetItemId) {
      setDraggingItemId(null); // Clear the dragging state even if dropped on itself
      return;
    }

    const sectionId = carePlan.sections[activeSection].id;
    const sectionIndex = carePlan.sections.findIndex(
      (section) => section.id === sectionId
    );

    if (sectionIndex === -1) return;

    const updatedItems = [...carePlan.sections[sectionIndex].items];

    const fromIndex = updatedItems.findIndex(
      (item) => item.id === draggingItemId
    );
    const toIndex = targetItemId
      ? updatedItems.findIndex((item) => item.id === targetItemId)
      : updatedItems.length;

    if (fromIndex === -1 || toIndex === -1) return;

    // Move the item
    const [draggedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, draggedItem);

    // Update the order property
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // Dispatch an action to update the section items in the Redux store
    dispatch(
      updateSectionItems({
        sectionId,
        items: reorderedItems,
      })
    );

    setDraggingItemId(null); // Reset the dragging item ID after drop
  };

  const handleDropIndicatorDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "1";
  };

  const handleDropIndicatorDragLeave = (e) => {
    // e.preventDefault();
    e.currentTarget.style.opacity = "0";
  };

  const sectionLabelMapping = {
    1: "NHS Number",
    2: "What my well looks like",
    3: "Things to help me stay well",
    4: "Symptoms i’m getting unwell",
    5: "Things to help me get better",
    6: "Urgent care",
  };

  const isBootstrapping =
    !sectionsLoaded ||
    !itemsLoaded ||
    !initialized ||
    carePlan.sections.length === 0;

  if (isBootstrapping) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  return (
    <>
      <FullScreenOverlay
        activeSection={activeSection}
        title="Create New Care Plan"
        width="100%"
        height="100%"
        buttons={[
          {
            label: "Close",
            onClick: handleClose,
            variant: "secondary",
            state: "idle",
          },
          {
            label: "Save",
            onClick: handleSave,
            variant: "primary",
          },
        ]}
      >
        <MainContainer>
          <Sidebar>
            {/* ─────────── DESKTOP: full list ─────────── */}
            <DesktopOnlyWrapper>
              {carePlan.sections.map((section, index) => (
                <SidebarItem
                  key={section.id}
                  $active={activeSection === index}
                  // if the section is 0 then check the nhsNumber is already validated if altered then don't allow to navigate
                  onClick={() =>
                    activeSection !== 0
                      ? setActiveSection(index)
                      : nhsNumber &&
                        nhsNumber === validatedNhsNumber &&
                        setActiveSection(index)
                  }
                >
                  {activeSection === index ? (
                    <CircleFilled color={section.color} />
                  ) : (
                    <CircleUnfilled />
                  )}
                  {sectionLabelMapping[section.id] || section.title}
                </SidebarItem>
              ))}
            </DesktopOnlyWrapper>

            {/* ─────────── MOBILE: arrows + single active ─────────── */}
            <MobileOnlyWrapper>
              {/* Up arrow: go back one, disabled at 0 */}
              <ArrowButton
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                disabled={activeSection === 0}
              >
                ↑
              </ArrowButton>

              {/* Only render the label once carePlan.sections[activeSection] is defined */}
              <SidebarItem $active onClick={() => {}}>
                {carePlan.sections?.[activeSection]
                  ? sectionLabelMapping[carePlan.sections[activeSection].id] ||
                    carePlan.sections[activeSection].title
                  : ""}
              </SidebarItem>

              {/* Down arrow: at section 0, only allow if NHS validated; else just next */}
              <ArrowButton
                onClick={() => {
                  if (activeSection === 0) {
                    if (nhsNumber && nhsNumber === validatedNhsNumber) {
                      setActiveSection(1);
                    }
                  } else {
                    setActiveSection(
                      Math.min(carePlan.sections.length - 1, activeSection + 1)
                    );
                  }
                }}
                disabled={
                  (activeSection === 0 &&
                    !(nhsNumber && nhsNumber === validatedNhsNumber)) ||
                  activeSection === carePlan.sections.length - 1
                }
              >
                ↓
              </ArrowButton>
            </MobileOnlyWrapper>
          </Sidebar>
          <FormWrapper ref={formRef}>
            <FormContainer>
              <FormTitleWrapper>
                <Rectangle color={carePlan.sections[activeSection]?.color} />
                <h5 className="semi-bold neutral-900">
                  {carePlan.sections[activeSection]?.title}
                </h5>
              </FormTitleWrapper>

              {carePlan.sections[activeSection]?.id === 1 ? (
                // NHS Number Section (Create Mode)
                <FormField>
                  <label
                    htmlFor="nhs-number"
                    className="body-text-regular medium neutral-800"
                  >
                    Enter your patient’s NHS number to continue
                  </label>
                  <NHSNumberInput
                    value={nhsNumber}
                    onChange={handleNHSNumberChange}
                  />
                </FormField>
              ) : carePlan.sections[activeSection]?.id === 6 ? (
                // Contact Page Section
                <UrgentCare
                  retrievedContacts={carePlan.contacts}
                  initialImportantInfo={carePlan.important_info}
                />
              ) : (
                // Other Sections
                <>
                  {carePlan.sections[activeSection]?.items?.length > 0 ? (
                    carePlan.sections[activeSection].items.map(
                      (item, index, array) => (
                        <React.Fragment
                          key={`${carePlan.sections[activeSection].id}-${item.id}`}
                        >
                          {carePlan.sections[activeSection]
                            ?.isServiceUserSpecific && (
                            <DropIndicator
                              beforeItemId={item.id}
                              onDragOver={handleDropIndicatorDragOver}
                              onDragLeave={handleDropIndicatorDragLeave}
                              onDrop={(e) => handleDrop(e, item.id)}
                              margin="-26px"
                            />
                          )}
                          <ItemContainer
                            draggable={isDraggable}
                            $isDragging={draggingItemId === item.id}
                            onDragStart={
                              isDraggable
                                ? (e) => handleDragStart(e, item.id)
                                : undefined
                            }
                            onDragEnd={() => setDraggingItemId(null)}
                          >
                            {/* ... Item content (as before) ... */}
                            <ItemTitle>
                              <h6 className="semi-bold neutral-900">
                                {item.title}
                              </h6>
                              {/* Show delete button for new items */}
                              {(String(item.id).startsWith("new-") ||
                                item.fromOverlay) && (
                                <IconButtonWrapper
                                  onClick={() => handleDeleteClick(item)}
                                >
                                  <IconButton
                                    variant="ghost"
                                    isRemove={true}
                                    noPadding={true}
                                    icon={<DeleteIcon color="#EC2D30" />}
                                  >
                                    Remove
                                  </IconButton>
                                </IconButtonWrapper>
                              )}
                              {item.user_defined && (
                                <IconButtonWrapper
                                  onClick={() => handleDeleteClick(item)}
                                >
                                  <IconButton
                                    variant="ghost"
                                    isRemove={true}
                                    noPadding={true}
                                    icon={<DeleteIcon color="#EC2D30" />}
                                  >
                                    Remove
                                  </IconButton>
                                </IconButtonWrapper>
                              )}
                            </ItemTitle>
                            <ItemInnerContainer>
                              <ItemThumbnailContainer>
                                <ItemThumbnail
                                  src={item?.image_url}
                                  alt={item?.title}
                                  width={"150px"}
                                  height={"150px"}
                                  borderRadius="12px"
                                  border="1px solid #d9d9d9"
                                />
                              </ItemThumbnailContainer>

                              <ItemDetailContainer>
                                <MultiSelectDropdown
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  prompt_text={
                                    item.section_prompt_overrides?.[
                                      currentSectionId
                                    ] ?? item.prompt_text
                                  }
                                  options={
                                    item.section_overrides?.[
                                      carePlan.sections[activeSection]?.id
                                    ] ?? []
                                  }
                                  value={item.answer || ""}
                                  onChange={(newValue) => {
                                    dispatch(
                                      updateAnswer({
                                        sectionId:
                                          carePlan.sections[activeSection].id,
                                        itemId: item.id,
                                        answer: newValue,
                                      })
                                    );
                                  }}
                                />
                              </ItemDetailContainer>
                            </ItemInnerContainer>
                          </ItemContainer>
                          {/* Conditionally render DropIndicator after the last item */}
                          {index === array.length - 1 &&
                            carePlan.sections[activeSection]
                              ?.isServiceUserSpecific && (
                              <DropIndicator
                                beforeItemId={null}
                                onDragOver={handleDropIndicatorDragOver}
                                onDragLeave={handleDropIndicatorDragLeave}
                                onDrop={(e) => handleDrop(e, null)}
                                margin="-26px"
                              />
                            )}
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <EmptyItemContainer>
                      <p className="body-text-regular regular neutral-600">
                        This topic doesn’t have any pre-defined sections. Start
                        adding from pre-default list or create user specific
                        sections
                      </p>
                      <IconButton
                        variant="primary"
                        state="idle"
                        width={"250px"}
                        type="button"
                        onClick={() => setShowAddItemOverlay(true)}
                      >
                        Select item from list
                      </IconButton>
                      <IconButton
                        variant="ghost"
                        state="idle"
                        icon={<PlusIcon color="#004F96" />}
                        iconPosition="left"
                        type="button"
                        onClick={() => setAddOtherItemOpen(true)}
                      >
                        Add other item
                      </IconButton>
                    </EmptyItemContainer>
                  )}
                </>
              )}
              {carePlan.sections[activeSection]?.id !== 1 &&
              carePlan.sections[activeSection]?.items?.length > 0 ? (
                carePlan.sections[activeSection]?.isServiceUserSpecific ? (
                  // Render the third component when all three conditions are true
                  <EmptyItemContainer>
                    <IconButton
                      variant="primary"
                      state="idle"
                      width={"250px"}
                      type="button" // Type is "button" because it is not related to form submission
                      onClick={() => setShowAddItemOverlay(true)}
                    >
                      Select item from list
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      state="idle"
                      icon={<PlusIcon color="#004F96" />}
                      iconPosition="left"
                      type="button" // Type is "button" because it is not related to form submission
                      onClick={() => setAddOtherItemOpen(true)}
                    >
                      Add other item
                    </IconButton>
                  </EmptyItemContainer>
                ) : (
                  // Render this component if only the first two conditions are true
                  <IconButton
                    variant="ghost"
                    state="idle"
                    width={"210px"}
                    icon={<PlusIcon color="#004F96" />}
                    iconPosition="left"
                    type="button"
                    onClick={() => setAddOtherItemOpen(true)}
                  >
                    Add other item
                  </IconButton>
                )
              ) : null}
            </FormContainer>

            <NavigationButtons
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              totalSections={carePlan.sections.length}
              handleContinue={handleContinue}
              handleBack={handleBack}
              handleFinish={() => setShowFinishConfirmModal(true)}
            />
          </FormWrapper>
        </MainContainer>

        {isAddOtherItemOpen && (
          <FullScreenOverlay
            iconClose={true}
            width="60%"
            height="370px"
            title="Add other item"
            isSlimModal={true}
            onClose={() => setAddOtherItemOpen(false)}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault(); // Prevent default form submission behavior
                handleAddNewItem();
              }}
            >
              <NewItemPopupContent>
                <p className="body-text-regular semi-bold neutral-800">
                  Item title
                </p>
                <p className="body-text-regular regular neutral-700">
                  Give the item a clear and descriptive title so users know what
                  it is about
                </p>
                <TextInputWrapper>
                  <TextField
                    placeholder="Enter your title"
                    maxLength={100}
                    showCharacterLimit={true}
                    name="title"
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    required
                  />
                </TextInputWrapper>
              </NewItemPopupContent>
              <Spacer height="24px" />
              <NewItemPopupContent>
                <Spacer height="24px" />
                <ButtonGroup>
                  <IconButton
                    variant="secondary"
                    onClick={() => setAddOtherItemOpen(false)}
                    state="idle"
                    width={"50%"}
                  >
                    Cancel
                  </IconButton>
                  <IconButton
                    variant="primary"
                    type="submit"
                    state="idle"
                    width={"50%"}
                  >
                    Add to Care Plan
                  </IconButton>
                </ButtonGroup>
              </NewItemPopupContent>
            </form>
          </FullScreenOverlay>
        )}
      </FullScreenOverlay>
      {showDeleteConfirmModal && itemToDelete && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirmModal(false);
                setItemToDelete(null);
                setDeleteText("");
              },
            },
            {
              label: "Yes, Remove",
              onClick: handleConfirmDelete, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm remove item</h5>
          <p>
            Are you sure you want to remove the item{" "}
            <span className="confirm-delete-nhs">{itemToDelete.title}</span>{" "}
            from this care plan? This action cannot be undone and will be
            permanently removed.
          </p>
          <p>
            Type <b>"Remove"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value);
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}

      {showFinishConfirmModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => handleFinishConfirmation(false),
            },
            {
              label: "Yes, Save",
              onClick: () => handleFinishConfirmation(true),
              variant: "primary",
              state: "idle",
              type: "button",
            },
          ]}
        >
          <h5 className="semi-bold">Save changes?</h5>
          <p>
            By proceeding, this Care Plan will be updated. You can accesss and
            edit under the Draft Care Plans list page.
          </p>
        </FullScreenOverlay>
      )}
      {/* Add Item Overlay */}
      {showAddItemOverlay && (
        <AddItemOverlay
          allItems={items}
          selectedItemIds={
            selectedItemIdsPerSection[carePlan.sections[activeSection].id] || []
          }
          onClose={() => setShowAddItemOverlay(false)}
          onUpdateSelectedItems={handleUpdateSelectedItems}
          addButtonText="Add to Care Plan"
        />
      )}
      {showNHSNumberWarning && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "OK",
              onClick: () => setShowNHSNumberWarning(false),
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">
            {validateNHSNumber(nhsNumber)
              ? "NHS Number Already Used"
              : "Invalid NHS Number"}
          </h5>
          <p>
            {validateNHSNumber(nhsNumber)
              ? `A care plan with NHS number ${nhsNumber} is already in draft or active state. Please use a different NHS number.`
              : "The NHS number format is incorrect. Please enter a valid NHS number in the format 123 456 7890."}
          </p>
        </FullScreenOverlay>
      )}

      {/* Confirmation for Close */}
      {showCloseConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="confirmation"
          buttons={[
            {
              label: "No",
              onClick: () => handleCloseConfirmation(false),
            },
            {
              label: "Yes",
              onClick: () => handleCloseConfirmation(true),
              variant: "primary",
            },
          ]}
        >
          <h5 className="semi-bold">Are you sure you want to close?</h5>
          <p>You will lose any unsaved changes.</p>
        </FullScreenOverlay>
      )}

      {/* Saved Confirmation */}
      {showSavedConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="save"
          buttons={[
            {
              label: "Overview",
              onClick: () => {
                onClose();
                go("overview");
              }, // Redirect to Dashboard Overview
              variant: "primary",
            },
            {
              label: "Draft plans",
              onClick: () => {
                // Redirect back to NHS number draft plan or care plan creation
                setShowSavedConfirmation(false);
                onClose();
                go("draft-care-plans");
                // logic to navigate to NHS number draft plan
              },
            },
          ]}
        >
          <h5 className="semi-bold">Care Plan Saved</h5>
          {savedToDb ? (
            <p>
              You’ve successfully saved the Care Plan {nhsNumber}. You can view
              it under Draft Care Plans.
            </p>
          ) : (
            <p>
              You’ve successfully saved the Care Plan {nhsNumber}. You can
              access it once you navigate to New Care Plan next time.
            </p>
          )}
        </FullScreenOverlay>
      )}
    </>
  );
};

export default CreateCarePlan;

// ... Styled components remain the same ...

const TextInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const IconButtonWrapper = styled.div``;

const MainContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 0px 40px;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0px 16px;
  }
`;

const Sidebar = styled.div`
  width: 20%;
  min-width: 312px;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  position: sticky;
  top: 0;

  @media (max-width: 768px) {
    // flex-direction: row; /* or whatever you need on mobile */
    width: 100%;
    min-width: auto;
    margin-bottom: 16px;
    /* display: none; Hide sidebar on mobile */
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  @media (max-width: 768px) {
    .desktop-list {
      display: none;
    }
  }

  @media (min-width: 769px) {
    .mobile-only-wrapper {
      display: none;
    }
  }
`;

const SidebarItem = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  width: 88%;
  border-radius: 10px;
  font-size: 16px;
  color: ${({ $active }) => ($active ? "#161718" : "#6C757D")};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  line-height: 20px;
  border: 1px solid ${({ $active }) => ($active ? "transparent" : "#e9ecef")};
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0px 5px 15px 0px rgba(0, 0, 0, 0.1)" : "none"};
`;

const FormContainer = styled.div`
  display: flex;
  flex: 1;
  padding: 24px;
  flex-direction: column;
  //   justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #fff;
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const FormWrapper = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  max-height: 100%;
`;

const FormTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Rectangle = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 100px;
  background: ${({ color }) => color || "#3a70e2"};
  width: 4px;
  max-width: 4px;
  max-height: 32px;
`;

const ItemContainer = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;

  /* drag cursor */
  cursor: ${({ draggable }) => (draggable ? "grab" : "default")};
  &:active {
    cursor: grabbing;
  }

  /* dynamic opacity */
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  /* but let buttons keep pointer */
  & *:not(button):not(input):not(textarea) {
    cursor: inherit;
  }
`;

const ItemTitle = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
`;

const ItemInnerContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ItemDetailContainer = styled.div`
  display: flex;
  padding: 16px 16px 16px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const ItemThumbnailContainer = styled.div`
  margin: 16px;
`;

const NewItemPopupContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const EmptyItemContainer = styled.div`
  display: flex;
  padding: 50px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px dashed #ced4da;

  p {
    max-width: 529px;
    text-align: center;
  }
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 12px;
  width: 100%;
  text-align: center;
  color: #6c757d;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  @media (min-width: 769px) {
    display: none; /* hide arrows on desktop */
  }
`;

const MobileOnlyWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`;

const DesktopOnlyWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;
